import React from 'react'
import { useTranslation } from 'react-i18next'
import { State } from '../store'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { Typography, Box, Paper, Tooltip } from '@mui/material'
import { SharedUsersPaginatedList } from './SharedUsersPaginatedList'
import { selectMembersWithAccess } from '../selectors/organizations'
import { selectOrganization } from '../selectors/organizations'
import { ShareButton } from '../buttons/ShareButton'
import { IconButton } from '../buttons/IconButton'
import { alphaSort } from '../helpers/utilHelper'
import { Gutters } from './Gutters'
import { Icon } from './Icon'

interface Props {
  device?: IDevice
  network?: INetwork
  users?: IUser[]
  connected?: IUser[]
}

export const SharedUsersLists: React.FC<Props> = ({ device, network, connected = [], users = [] }) => {
  const { t } = useTranslation()
  const instance = device || network
  const location = useLocation()
  const members = useSelector((state: State) => selectMembersWithAccess(state, undefined, instance)).map(m => m.user)
  const hasOrganization = useSelector((state: State) => !!selectOrganization(state).id)
  const disconnected = users.filter(user => !connected.find(_u => _u.email === user.email))
  const manager = !!instance?.permissions.includes('MANAGE')

  if (!connected.length && !disconnected.length && !members.length)
    return (
      <Paper elevation={0} sx={{ contain: 'layout', marginTop: 2 }}>
        <Gutters center bottom="xxl" top="lg">
          <Box paddingBottom={4} paddingTop={4}>
            <Tooltip title={t('sharedUsersLists.noAccess', 'No one has access to this service.')} placement="top" arrow>
              <Icon name="user-slash" type="light" fontSize={28} color="gray" />
            </Tooltip>
          </Box>
          <ShareButton to={location.pathname.replace('users', 'share')} iconInlineLeft>
            <Typography variant="body2">{t('sharedUsersLists.shareToGuest', 'Share to a guest')}</Typography>
          </ShareButton>
          <Typography variant="caption" color="gray.main" component="div">
            {t('sharedUsersLists.or', 'or')}
          </Typography>
          {hasOrganization ? (
            <IconButton
              icon="user-plus"
              to="/organization/add"
              size="md"
              disabled={!instance?.permissions.includes('ADMIN')}
            >
              <Typography variant="body2">
                {' '}
                &nbsp;&nbsp; {t('sharedUsersLists.addOrganizationMember', 'Add an organization member')}
              </Typography>
            </IconButton>
          ) : (
            <IconButton icon="industry-alt" to="/organization" size="md">
              <Typography variant="body2">
                {' '}
                &nbsp;&nbsp; {t('sharedUsersLists.createOrganization', 'Create your organization')}
              </Typography>
            </IconButton>
          )}
        </Gutters>
      </Paper>
    )

  return (
    <>
      <SharedUsersPaginatedList
        title={t('sharedUsersLists.connected', 'Connected')}
        device={device}
        users={connected}
        connected
      />
      <SharedUsersPaginatedList
        title={t('sharedUsersLists.guests', 'Guests')}
        device={device}
        remove={network?.id}
        users={sort(disconnected)}
      />
      {manager && (
        <SharedUsersPaginatedList
          title={t('sharedUsersLists.organizationMembers', 'Organization members')}
          device={device}
          users={sort(members)}
          members
        />
      )}
    </>
  )
}

const sort = (users: IUser[]) => users.sort((a, b) => alphaSort(a.email, b.email))
